import React, { useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  checkInputErrorMaxLength,
  putCookie,
  useAutocomplete,
  useEffectOnce,
  useInput,
  useQuery,
  useSelect
} from '@chilecompra/react-kit'
import { Autocomplete, Input, Select, Typography } from '@chilecompra/react-kit/components'
import {
  QUOTE_SEARCH_FILTER_INITIAL_PAGE,
  QUOTE_SEARCH_FILTER_INITIAL_VALUES,
  QUOTE_SEARCH_ORDERS
} from '../../config/settings/constants'
import routes from '../../config/settings/routes'
import { updateQuery } from '../../modules/utils/searches'
import OtherFilters from './QuoteSearchFilters.others'
import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import { useConfig } from '../ConfigProvider/ConfigProvider.hook'
import { onGetOrganizationThunk, onGetOrganizationsThunk } from './QuoteSearchFilters.actions'
import {
  onForcePriorizedAlready,
  onGetQuoteSearchThunk
} from '../QuoteSearchHeadingFilter/QuoteSearchHeadingFilters.actions'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  ClearIcon,
  ExpandMoreIcon,
  Grid,
  Hidden,
  SearchIcon
} from './QuoteSearchFilters.styles'

/*
 * The DesktopView's component.
 */
const DesktopView = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const query = useQuery()
  const { isBuyer, isSeller } = useAuthorization()
  const { regionInfo, userUnitsInfo } = useConfig()
  const firstLoadRef = useRef(true)

  const {
    forcePriorizedAlready,
    loading: searchLoading,
    supplierPrioritized
  } = useSelector(state => state.quoteSearchHeadingFilters)
  const { loading: byOrganizationsLoading, organizations: byOrganizationsOptions } = useSelector(
    state => state.quoteSearchFilters
  )

  const {
    error: orderError,
    value: orderValue,
    onChange: handleOrderChange
  } = useSelect({
    changeCallback: updateValues => {
      handleSearch({ byIdValue, byOrganizationOption, orderValue: updateValues, organizationValue, regionValue })
    },
    initialValue: query.has('orderBy') ? query.get('orderBy') : QUOTE_SEARCH_FILTER_INITIAL_VALUES.orderBy,
    overwriteCallback: () => (query.has('orderBy') ? query.get('orderBy') : QUOTE_SEARCH_FILTER_INITIAL_VALUES.orderBy)
  })
  const { value: regionValue, onChange: handleRegionChange } = useSelect({
    changeCallback: updateValues => {
      handleSearch({ byIdValue, byOrganizationOption, orderValue, organizationValue, regionValue: updateValues })
    },
    initialValue: query.has('region') ? query.get('region') : QUOTE_SEARCH_FILTER_INITIAL_VALUES.region,
    overwriteCallback: () => (query.has('region') ? query.get('region') : QUOTE_SEARCH_FILTER_INITIAL_VALUES.region)
  })
  const {
    error: byOrganizationError,
    option: byOrganizationOption,
    value: byOrganizationValue,
    onChange: handleByOrganizationChange,
    onSelect: handleByOrganizationSelect
  } = useAutocomplete({
    changeCallback: updatedValue =>
      updatedValue?.length >= 3 && dispatch(onGetOrganizationsThunk({ pattern: updatedValue })),
    errorCallbacks: [checkInputErrorMaxLength(255)],
    initialOption: query.has('organization')
      ? { name: '', value: Number.parseInt(query.get('organization'), 10) }
      : null,
    initialValue: query.has('organization')
      ? query.get('organization')
      : QUOTE_SEARCH_FILTER_INITIAL_VALUES.organization,
    overwriteCallback: () =>
      query.has('organization')
        ? byOrganizationsOptions.find(option => option.value === Number.parseInt(query.get('organization'), 10))
        : null,
    selectCallback: updatedOption =>
      handleSearch({
        byIdValue,
        byOrganizationOption: updatedOption,
        orderValue,
        organizationValue,
        regionValue
      })
  })
  const {
    error: byIdError,
    value: byIdValue,
    onChange: handleByIdChange,
    setValue: setByIdValue
  } = useInput({
    changeCallback: updatedValue => setByIdValue(updatedValue.trim()),
    errorCallbacks: [checkInputErrorMaxLength(20)],
    initialValue: query.has('id') ? query.get('id') : QUOTE_SEARCH_FILTER_INITIAL_VALUES.id,
    overwriteCallback: () => (query.has('id') ? query.get('id') : QUOTE_SEARCH_FILTER_INITIAL_VALUES.id)
  })
  const {
    error: organizationError,
    value: organizationValue,
    onChange: handleOrganizationChange
  } = useSelect({
    changeCallback: updateValues => {
      handleSearch({ byIdValue, byOrganizationOption, orderValue, organizationValue: updateValues, regionValue })
    },
    initialValue: query.has('organization')
      ? query.get('organization')
      : QUOTE_SEARCH_FILTER_INITIAL_VALUES.organization,
    overwriteCallback: () =>
      query.has('organization') ? query.get('organization') : QUOTE_SEARCH_FILTER_INITIAL_VALUES.organization
  })

  const handleByIdKeyDown = event =>
    event.key === 'Enter' &&
    handleSearch({ byIdValue, byOrganizationOption, orderValue, organizationValue, regionValue })
  const handleByIdClick = () =>
    handleSearch({
      byIdValue: query.has('id') ? '' : byIdValue,
      byOrganizationOption,
      orderValue,
      organizationValue,
      regionValue
    })
  const handleSearch = updatedValues => {
    let dictionary = {
      ...QUOTE_SEARCH_FILTER_INITIAL_VALUES,
      ...Object.fromEntries(query.entries()),
      id: updatedValues.byIdValue,
      orderBy: updatedValues.orderValue,
      region: updatedValues.regionValue
    }

    if (isBuyer) {
      dictionary.organization = updatedValues.organizationValue
    }

    if (isSeller) {
      dictionary.organization = updatedValues.byOrganizationOption?.value || ''
      /** force priority search */
      if (supplierPrioritized?.isNoEMT && forcePriorizedAlready) {
        dictionary = {
          ...dictionary,
          priorized: '1'
        }
        dispatch(onForcePriorizedAlready())
      }
    }

    if (!firstLoadRef.current) {
      query.set('page', QUOTE_SEARCH_FILTER_INITIAL_PAGE)
    } else {
      firstLoadRef.current = false
    }

    updateQuery(query, dictionary)
    putCookie('search', query.toString())
    navigate({ pathname: routes.search, search: query.toString() }, { replace: true })
    handleSearchActionThunk(query)
  }

  useEffectOnce(() => {
    if (isBuyer || !query.has('organization')) {
      return
    }
    dispatch(onGetOrganizationThunk({ id: byOrganizationOption?.value }))
  })

  const handleSearchActionThunk = query => {
    dispatch(onGetQuoteSearchThunk({ query }))
  }

  return (
    <>
      <Grid container margin="33px 0 0">
        <Grid item>
          <Select
            error={orderError}
            label="Ordenar resultados"
            loading={searchLoading}
            options={QUOTE_SEARCH_ORDERS || []}
            value={orderValue}
            onChange={handleOrderChange}
          />
        </Grid>
      </Grid>
      <Accordion defaultExpanded elevation={0}>
        {isSeller && (
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content">
            <Typography variant="h4" fontWeight="bold">
              Filtros destacados
            </Typography>
          </AccordionSummary>
        )}
        <AccordionDetails>
          <Hidden>
            <Input
              error={byIdError}
              label="Buscar por ID"
              loading={searchLoading}
              suffix={
                query.has('id') ? (
                  <ClearIcon onClick={handleByIdClick} />
                ) : (
                  <SearchIcon color="blue" onClick={handleByIdClick} />
                )
              }
              value={byIdValue}
              onChange={handleByIdChange}
              onKeyDown={handleByIdKeyDown}
            />
          </Hidden>
          {isBuyer && (
            <ProtectByFeatures rule={features => features['organization.select']}>
              <Select
                error={organizationError}
                label="Unidad de Compra"
                loading={searchLoading || userUnitsInfo?.loading}
                options={userUnitsInfo.units || []}
                placeholder="Todos"
                value={organizationValue}
                onChange={handleOrganizationChange}
              />
            </ProtectByFeatures>
          )}
          {isSeller && (
            <Select
              label="Región"
              loading={searchLoading || regionInfo.loading}
              options={regionInfo.regions || []}
              placeholder="Todos"
              value={regionValue}
              onChange={handleRegionChange}
            />
          )}
          {isSeller && (
            <Autocomplete
              error={byOrganizationError}
              label="Organismo comprador"
              loading={searchLoading}
              option={byOrganizationOption}
              options={byOrganizationsOptions}
              value={byOrganizationValue}
              waiting={byOrganizationsLoading}
              onChange={handleByOrganizationChange}
              onSelect={handleByOrganizationSelect}
            />
          )}
        </AccordionDetails>
      </Accordion>

      <OtherFilters
        forcePriorized={forcePriorizedAlready}
        isBuyer={isBuyer}
        isLoading={searchLoading}
        isNoEMT={supplierPrioritized?.isNoEMT || false}
        isSeller={isSeller}
        handleSearchActionThunk={handleSearchActionThunk}
      />
    </>
  )
}

export default DesktopView
